import type { PanelId } from "./types";

/**
 * Maps each PanelId to its i18n chip label key.
 * Used by ActionBar (chip buttons) and WishesAndSuggestionsPanel (label fallback).
 */
export const BUTTON_LABEL_KEYS: Record<PanelId, string> = {
  interiorFinishing: "interiorDesigner.chips.interiorFinishing",
  furnishing: "interiorDesigner.chips.furnishing",
  wallColor: "interiorDesigner.chips.wallColor",
  otherWishes: "interiorDesigner.chips.wishesAndSuggestions",
  history: "interiorDesigner.chips.history",
};

/**
 * Maps each PanelId to its HugeIcons icon name.
 * Used by ActionBar (chip buttons) and WishesAndSuggestionsPanel (per-entry icon).
 */
export const BUTTON_ICON_NAMES: Record<PanelId, string> = {
  interiorFinishing: "StrokeInsideIcon",
  furnishing: "Cabinet01Icon",
  wallColor: "PaintBrush02Icon",
  otherWishes: "AiMagicIcon",
  history: "Clock01Icon",
};

/**
 * Panels that have a rendered UI. Other panels dispatch PANEL_OPENED but have
 * no component yet, so their chips/cards are disabled.
 */
export const IMPLEMENTED_PANELS: ReadonlySet<PanelId> = new Set([
  "interiorFinishing",
  "furnishing",
  "wallColor",
  "otherWishes",
]);

export const DEFAULT_ROOM_TYPE = "living_room";

/**
 * Maps Dutch classification room names (from the backend LLM classifier)
 * to English vocab IDs (from GET /interior-designer/vocab).
 * Keys are lowercase for case-insensitive matching.
 */
const CLASSIFICATION_TO_VOCAB_ROOM_TYPE: Record<string, string> = {
  woonkamer: "living_room",
  slaapkamer: "bedroom",
  keuken: "kitchen",
  eetkamer: "dining_room",
  kinderkamer: "kids_room",
  studeerkamer: "study",
  badkamer: "bathroom",
};

/**
 * Resolves a backend classification room_type to its vocab ID.
 * Falls back to the raw value if it's already a vocab ID or unknown.
 */
export const resolveRoomTypeVocabId = ({
  classificationRoomType,
}: {
  classificationRoomType: string | null;
}): string | null => {
  if (classificationRoomType === null) return null;
  return (
    CLASSIFICATION_TO_VOCAB_ROOM_TYPE[classificationRoomType.toLowerCase()] ??
    classificationRoomType
  );
};

/**
 * Room types where the FurnishingPanel room-type selector is disabled.
 * Uses English vocab IDs — callers resolve classification values first.
 */
export const LOCKED_ROOM_TYPES: ReadonlySet<string> = new Set([
  "bathroom",
  "kitchen",
]);
